import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Mail, Lock, User, Building2, MapPin, Phone, ArrowLeft, 
  Home, Hash, Globe 
} from "lucide-react";
import axios from "axios";

const api = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL,
});

const RegisterPage = () => {
  const navigate = useNavigate();
  const { setUser, setToken } = useAuth();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 2-step registration
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    passwordConfirm: "",
    ansprechpartner: "",
    firmenname: "",
    strasse: "",
    hausnummer: "",
    plz: "",
    stadt: "",
    land: "Deutschland",
    telefon: ""
  });

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const validateStep1 = () => {
    if (!formData.email || !formData.password || !formData.passwordConfirm) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return false;
    }
    if (formData.password.length < 8) {
      toast.error("Passwort muss mindestens 8 Zeichen haben");
      return false;
    }
    if (formData.password !== formData.passwordConfirm) {
      toast.error("Passwörter stimmen nicht überein");
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!formData.ansprechpartner || !formData.strasse || !formData.hausnummer || 
        !formData.plz || !formData.stadt || !formData.land) {
      toast.error("Bitte alle Pflichtfelder ausfüllen");
      return false;
    }
    return true;
  };

  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep2()) return;
    
    setLoading(true);
    try {
      const response = await api.post("/api/auth/register-extended", {
        email: formData.email,
        password: formData.password,
        ansprechpartner: formData.ansprechpartner,
        firmenname: formData.firmenname || null,
        strasse: formData.strasse,
        hausnummer: formData.hausnummer,
        plz: formData.plz,
        stadt: formData.stadt,
        land: formData.land,
        telefon: formData.telefon || null
      });
      
      // Registration requires email verification - redirect to success page
      if (response.data.requires_verification) {
        toast.success("Registrierung erfolgreich! Bitte bestätigen Sie Ihre E-Mail.");
        navigate("/registration-success");
      } else {
        // Fallback for old behavior (shouldn't happen with new code)
        localStorage.setItem("token", response.data.token);
        localStorage.setItem("user", JSON.stringify(response.data.user));
        setToken(response.data.token);
        setUser(response.data.user);
        navigate("/");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registrierung fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen login-bg flex items-center justify-center p-4" data-testid="register-page">
      <div className="login-overlay absolute inset-0"></div>
      
      <Card className="w-full max-w-lg relative z-10 shadow-xl border-0 animate-fadeIn">
        <CardHeader className="text-center pb-2">
          <img src="/vytech-logo.png" alt="VyWork" className="h-16 w-auto mx-auto mb-2" />
          <CardTitle className="text-2xl font-bold">
            Konto erstellen
          </CardTitle>
          <CardDescription className="text-muted-foreground">
            {step === 1 ? "Schritt 1/2: Zugangsdaten" : "Schritt 2/2: Firmendaten"}
          </CardDescription>
          
          {/* Progress indicator */}
          <div className="flex gap-2 justify-center mt-4">
            <div className={`h-2 w-16 rounded-full ${step >= 1 ? 'bg-primary' : 'bg-muted'}`}></div>
            <div className={`h-2 w-16 rounded-full ${step >= 2 ? 'bg-primary' : 'bg-muted'}`}></div>
          </div>
        </CardHeader>

        <CardContent>
          {step === 1 ? (
            <form onSubmit={(e) => { e.preventDefault(); handleNextStep(); }} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-Mail-Adresse *</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="firma@beispiel.de"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    required
                    data-testid="register-email"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Passwort *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={formData.password}
                    onChange={(e) => updateField("password", e.target.value)}
                    required
                    minLength={8}
                    data-testid="register-password"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Mindestens 8 Zeichen</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="passwordConfirm">Passwort bestätigen *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="passwordConfirm"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={formData.passwordConfirm}
                    onChange={(e) => updateField("passwordConfirm", e.target.value)}
                    required
                    data-testid="register-password-confirm"
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                className="w-full h-11 btn-primary"
                data-testid="register-next-step"
              >
                Weiter
              </Button>
              
              <div className="text-center text-sm text-muted-foreground">
                Bereits ein Konto?{" "}
                <Link to="/login" className="text-primary hover:underline">
                  Anmelden
                </Link>
              </div>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="ansprechpartner">Ansprechpartner *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="ansprechpartner"
                    type="text"
                    placeholder="Max Mustermann"
                    className="pl-10"
                    value={formData.ansprechpartner}
                    onChange={(e) => updateField("ansprechpartner", e.target.value)}
                    required
                    data-testid="register-contact"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="firmenname">Firmenname (optional)</Label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="firmenname"
                    type="text"
                    placeholder="Muster GmbH"
                    className="pl-10"
                    value={formData.firmenname}
                    onChange={(e) => updateField("firmenname", e.target.value)}
                    data-testid="register-company"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="strasse">Straße *</Label>
                  <div className="relative">
                    <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="strasse"
                      type="text"
                      placeholder="Musterstraße"
                      className="pl-10"
                      value={formData.strasse}
                      onChange={(e) => updateField("strasse", e.target.value)}
                      required
                      data-testid="register-street"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hausnummer">Nr. *</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="hausnummer"
                      type="text"
                      placeholder="1a"
                      className="pl-10"
                      value={formData.hausnummer}
                      onChange={(e) => updateField("hausnummer", e.target.value)}
                      required
                      data-testid="register-house-number"
                    />
                  </div>
                </div>
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="plz">PLZ *</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="plz"
                      type="text"
                      placeholder="12345"
                      className="pl-10"
                      value={formData.plz}
                      onChange={(e) => updateField("plz", e.target.value)}
                      required
                      data-testid="register-postal"
                    />
                  </div>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="stadt">Stadt *</Label>
                  <Input
                    id="stadt"
                    type="text"
                    placeholder="Musterstadt"
                    value={formData.stadt}
                    onChange={(e) => updateField("stadt", e.target.value)}
                    required
                    data-testid="register-city"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label htmlFor="land">Land *</Label>
                  <Select 
                    value={formData.land} 
                    onValueChange={(value) => updateField("land", value)}
                  >
                    <SelectTrigger data-testid="register-country">
                      <Globe className="w-4 h-4 mr-2 text-muted-foreground" />
                      <SelectValue placeholder="Land wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Deutschland">Deutschland</SelectItem>
                      <SelectItem value="Österreich">Österreich</SelectItem>
                      <SelectItem value="Schweiz">Schweiz</SelectItem>
                      <SelectItem value="Andere">Andere</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="telefon">Telefon (optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="telefon"
                      type="tel"
                      placeholder="+49 123 456789"
                      className="pl-10"
                      value={formData.telefon}
                      onChange={(e) => updateField("telefon", e.target.value)}
                      data-testid="register-phone"
                    />
                  </div>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setStep(1)}
                  className="flex-1"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Zurück
                </Button>
                <Button
                  type="submit"
                  className="flex-1 btn-primary"
                  disabled={loading}
                  data-testid="register-submit"
                >
                  {loading ? "Wird erstellt..." : "Registrieren"}
                </Button>
              </div>
              
              <p className="text-xs text-center text-muted-foreground">
                Mit der Registrierung akzeptieren Sie unsere Nutzungsbedingungen.
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
